import { useEffect, useRef, useState } from "react";
import { baseURL, imageURL } from "services/API";

const PREVIEW_DEBOUNCE_MS = 500;

export const formatPreviewPhoneNumber = (contact) => {
  if (!contact) return "";
  if (contact.startsWith("+256")) {
    return `0${contact.slice(4)}`;
  }
  if (contact.startsWith("256")) {
    return `0${contact.slice(3)}`;
  }
  return contact;
};

const hasRequiredPreviewFields = ({ business_name, first_name, last_name, email, contact }) =>
  Boolean(
    business_name?.trim() &&
      first_name?.trim() &&
      last_name?.trim() &&
      email?.trim() &&
      contact?.trim()
  );

const resolvePreviewUrl = (previewDocumentUrl) => {
  if (!previewDocumentUrl) return "";
  if (previewDocumentUrl.startsWith("http")) {
    return previewDocumentUrl;
  }
  return `${imageURL}${previewDocumentUrl}`;
};

const useAgreementPreview = ({ values, setFieldValue }) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const requestIdRef = useRef(0);

  const accountFields = {
    business_name: values.business_name,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    contact: values.contact,
  };

  useEffect(() => {
    if (!hasRequiredPreviewFields(accountFields)) {
      setPreviewUrl("");
      setFieldValue("agreement_template_id", "");
      setFieldValue("terms_agreed", false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const timeoutId = setTimeout(async () => {
      setFieldValue("agreement_template_id", "");
      setFieldValue("terms_agreed", false);

      try {
        const response = await fetch(`${baseURL}/landlords/agreements/preview/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source_channel: "SELF_SERVICE",
            first_name: accountFields.first_name.trim(),
            last_name: accountFields.last_name.trim(),
            business_name: accountFields.business_name.trim(),
            phone_number: formatPreviewPhoneNumber(accountFields.contact.trim()),
            username: accountFields.contact.trim(),
          }),
        });

        const result = await response.json();

        if (requestId !== requestIdRef.current) {
          return;
        }

        const previewDocumentUrl = result.data?.preview_document_url;
        const isSuccess =
          (result.ok === true || result.status === 200) && Boolean(previewDocumentUrl);

        if (isSuccess) {
          setPreviewUrl(resolvePreviewUrl(previewDocumentUrl));
          setFieldValue("agreement_template_id", result.data.agreement_template_id);
        } else {
          setPreviewUrl("");
        }
      } catch (previewError) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setPreviewUrl("");
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [
    accountFields.business_name,
    accountFields.first_name,
    accountFields.last_name,
    accountFields.email,
    accountFields.contact,
    setFieldValue,
  ]);

  return { previewUrl };
};

export default useAgreementPreview;
